import { useFirestoreGeneralOrderBy } from '../../firebase/useFirestore';
import useMsiEffectTitle from '../../hooks/useMsiEffectTitle';

/**
 * Shared, presentational result cards for the redesigned research-results views.
 * Each card takes its already-fetched `responses` array as a prop so the same
 * card can back the Forms (by Timestamp), Sessions (by SessionID) and any other
 * view — only the response query differs per page. Field-scoped data (matrix
 * columns, choice options) is the same regardless of page, so cards fetch that
 * themselves.
 *
 * @typedef {object} QuestionnaireField
 * @property {string} ID
 * @property {string} docid
 * @property {string} Question
 * @property {string} Type
 * @property {number} [ReachStart]
 * @property {number} [ReachEnd]
 */

/**
 * The head of a result card: type label, the effect this question measures, and
 * the question itself.
 *
 * Meetstandaard (MSI) questions carry an empty or generic `Question` — their
 * real name lives on the linked `OutputEffects` doc. Without it the card head
 * shows only the matrix statement, and the reader cannot tell which effect the
 * answers belong to. When the field has its own question text the effect is
 * shown above it; when it hasn't, the effect name IS the heading.
 *
 * @param {{ field: QuestionnaireField, label: string, pill?: React.ReactNode }} props
 */
const CardHead = ({ field, label, pill }) => {
    const effectTitle = useMsiEffectTitle(field.EffectId);
    const question = String(field.Question || '').trim();

    return (
        <div className="rrf-card-head">
            <div>
                <div className="rrf-q-label">{label}</div>
                {effectTitle && question && (
                    <div className="rrf-effect-title">{effectTitle}</div>
                )}
                <h3>{question || effectTitle}</h3>
            </div>
            {pill}
        </div>
    );
};

/** Small check used inside a selected multiple-choice chip. */
const CheckIcon = () => (
    <svg viewBox="0 0 24 24"><path d="M5 13l4 4L19 7" /></svg>
);

/**
 * Maps a 1-based column index to one of three colour bands
 * (1 = coral / low, 2 = amber / mid, 3 = teal / high).
 * @param {number | null} index - 1-based position of the answered column
 * @param {number} total - Total number of columns in the matrix
 * @returns {1 | 2 | 3 | null}
 */
const colourBand = (index, total) => {
    if (!index || !total) return null;
    if (index <= total / 3) return 1;
    if (index <= (2 * total) / 3) return 2;
    return 3;
};

/**
 * Matrix question card — each statement (row) shows its score as a coloured
 * meter and badge.
 * @param {{ field: QuestionnaireField, responses: any[] }} props
 */
const MatrixCard = ({ field, responses }) => {
    const columns = useFirestoreGeneralOrderBy(
        'MatrixQuestionColumns', 'Field', field.ID ? field.ID : '', 'Position', 'asc'
    );

    if (!responses) return null;

    const cols = Array.isArray(columns) ? columns : [];
    const colCount = cols.length;
    const rows = Array.isArray(responses) ? responses : [];

    // 1-based position of the answered column (by ID, then title, then numeric Input).
    const columnIndex = (r) => {
        if (colCount) {
            const byId = cols.findIndex((c) => c.ID === r.ColumnID);
            if (byId >= 0) return byId + 1;
            const byTitle = cols.findIndex((c) => c.Title === r.Input);
            if (byTitle >= 0) return byTitle + 1;
        }
        const n = Number(r.Input);
        return Number.isFinite(n) && r.Input !== '' ? n : null;
    };

    return (
        <div className="rrf-card">
            <CardHead
                field={field}
                label={`Matrixvraag${colCount ? ` · schaal 1–${colCount}` : ''}`}
                pill={rows.length > 0 && (
                    <span className="rrf-count-pill">
                        {rows.length} {rows.length === 1 ? 'stelling' : 'stellingen'}
                    </span>
                )}
            />

            {colCount > 0 && (
                <div className="rrf-legend">
                    {cols.map((c, i) => {
                        const title = String(c.Title).trim();
                        const label = title === String(i + 1) ? title : `${i + 1} · ${title}`;
                        return (
                            <span className="rrf-legend-item" key={c.ID || i}>
                                <span className="rrf-legend-dot" data-band={colourBand(i + 1, colCount)} />
                                {label}
                            </span>
                        );
                    })}
                </div>
            )}

            <div className="rrf-statements">
                {rows.length === 0 ? (
                    <span className="rrf-empty">Geen antwoorden.</span>
                ) : (
                    rows.map((r) => {
                        const index = columnIndex(r);
                        const band = colourBand(index, colCount || 3);
                        return (
                            <div className="rrf-stmt" data-score={band || undefined} key={r.ID || r.docid}>
                                <span className="rrf-stmt-text">
                                    {r.RowTitle}{r.RowTitleRight ? ` ↔ ${r.RowTitleRight}` : ''}
                                </span>
                                {colCount > 0 && index ? (
                                    <span className="rrf-meter">
                                        {cols.map((c, i) => (
                                            <span className={`rrf-seg${i < index ? ' on' : ''}`} key={c.ID || i} />
                                        ))}
                                    </span>
                                ) : null}
                                <span className="rrf-score-badge">
                                    {index != null ? index : String(r.Input)}
                                </span>
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
};

/**
 * Scale / number question card — shows the value big with a filled track.
 * @param {{ field: QuestionnaireField, responses: any[] }} props
 */
const ScaleCard = ({ field, responses }) => {
    if (!responses) return null;

    const rows = Array.isArray(responses) ? responses : [];
    const value = rows.length ? Number(rows[0].Input) : null;
    const isScale = field.Type === 'scale';
    const start = Number.isFinite(Number(field.ReachStart)) ? Number(field.ReachStart) : 0;
    const end = Number.isFinite(Number(field.ReachEnd)) ? Number(field.ReachEnd) : null;
    const range = end != null ? (end - start || 1) : 1;
    const fillPct = value != null && end != null
        ? Math.max(0, Math.min(100, ((value - start) / range) * 100))
        : 0;

    return (
        <div className="rrf-card">
            <CardHead
                field={field}
                label={isScale ? `Schaalvraag · ${start}–${end != null ? end : '?'}` : 'Getalvraag'}
            />

            {value == null ? (
                <span className="rrf-empty">Geen antwoord.</span>
            ) : (
                <div className="rrf-scale-row">
                    <span className="rrf-scale-num">
                        <b>{value}</b>
                        {isScale && end != null ? <span>&nbsp;/ {end}</span> : null}
                    </span>
                    {isScale && end != null ? (
                        <span className="rrf-scale-track">
                            <span className="rrf-scale-fill" style={{ width: `${fillPct}%` }} />
                        </span>
                    ) : null}
                </div>
            )}
        </div>
    );
};

/**
 * Multiple-choice question card — all options shown, the answered ones marked.
 * @param {{ field: QuestionnaireField, responses: any[] }} props
 */
const ChoiceCard = ({ field, responses }) => {
    const options = useFirestoreGeneralOrderBy(
        'MultipleQuestionOptions', 'Field', field.ID ? field.ID : '', 'Position', 'asc'
    );

    if (!responses) return null;

    const rows = Array.isArray(responses) ? responses : [];
    const opts = Array.isArray(options) ? options : [];
    const single = field.Type === 'multiple-one';

    const isSelected = (opt) =>
        rows.some((r) => r.Itemid === opt.ID || (r.Input !== '' && r.Input === opt.Option));

    // 'Anders, namelijk…' staat opgeslagen als Option 'other'; de invuller zag
    // de Nederlandse tekst, en wat hij erbij typte hoort erachter (testronde
    // 2026-10-15, B6). Zelfde tekst als op het invulscherm (FieldType.jsx).
    /** @param {{ID: string, Option: string}} opt @returns {string} */
    const optionLabel = (opt) => {
        if (opt.Option !== 'other') return opt.Option;
        const typed = rows.find((r) => r.Itemid === opt.ID && r.OptionType === 'other' && String(r.Input ?? '').trim() !== '');
        return typed ? `Anders, namelijk...: ${typed.Input}` : 'Anders, namelijk...';
    };

    const chips = opts.length
        ? opts.map((o) => ({ key: o.ID, label: optionLabel(o), sel: isSelected(o) }))
        : rows.map((r, i) => ({ key: r.ID || i, label: String(r.Input), sel: true }));

    return (
        <div className="rrf-card">
            <CardHead
                field={field}
                label={`Meerkeuze · ${single ? 'één antwoord' : 'meerdere antwoorden'}`}
            />

            {chips.length === 0 ? (
                <span className="rrf-empty">Geen antwoord.</span>
            ) : (
                <div className="rrf-choices">
                    {chips.map((c) => (
                        <span className={`rrf-choice${c.sel ? ' sel' : ''}`} key={c.key}>
                            {c.sel && (
                                <span className="rrf-check"><CheckIcon /></span>
                            )}
                            {c.label}
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
};

/**
 * Open / paragraph question card — answers shown as quotes.
 * @param {{ field: QuestionnaireField, responses: any[] }} props
 */
const ParagraphCard = ({ field, responses }) => {
    if (!responses) return null;

    const rows = (Array.isArray(responses) ? responses : []).filter((r) => String(r.Input).trim() !== '');

    return (
        <div className="rrf-card">
            <CardHead field={field} label="Open vraag" />

            {rows.length === 0 ? (
                <span className="rrf-empty">Geen antwoord.</span>
            ) : (
                rows.map((r) => (
                    <div className="rrf-answer-quote" key={r.ID || r.docid}>{String(r.Input)}</div>
                ))
            )}
        </div>
    );
};

/**
 * Dispatches a single field to the card matching its type.
 * @param {{ field: QuestionnaireField, responses: any[] }} props
 */
const QuestionCard = ({ field, responses }) => {
    switch (field.Type) {
        case 'matrix-one':
        case 'matrix-multiple':
            return <MatrixCard field={field} responses={responses} />;
        case 'scale':
        case 'number':
            return <ScaleCard field={field} responses={responses} />;
        case 'multiple-one':
        case 'multiple-multiple':
            return <ChoiceCard field={field} responses={responses} />;
        case 'paragraph':
            return <ParagraphCard field={field} responses={responses} />;
        default:
            return null;
    }
};

export { CardHead, MatrixCard, ScaleCard, ChoiceCard, ParagraphCard, QuestionCard };
export default QuestionCard;
